import React, { useLayoutEffect, useRef, useCallback } from 'react';
import { useIOSStatusBar } from '@/hooks/useIOSStatusBar';

interface PageWithHeaderProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
}

/**
 * HOC for consistent iOS header + status bar across all pages
 * Ensures:
 * - Status bar is always black (#000000)
 * - Content never overlaps with status bar
 * - Header is fixed at top
 * - Dynamic height calculation for proper content offset (optimized with RAF)
 * - Smooth scroll behavior
 */
export function PageWithHeader({ children, header, className = '' }: PageWithHeaderProps) {
  useIOSStatusBar();
  const headerRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);

  const calculateOffset = useCallback(() => {
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Schedule update with RAF to batch DOM reads/writes
    rafIdRef.current = requestAnimationFrame(() => {
      const now = Date.now();
      
      // Throttle: only update if 16ms (1 frame) has passed
      if (now - lastUpdateRef.current < 16) {
        return;
      }
      
      lastUpdateRef.current = now;
      
      // Measure the actual fixed header element (first child) to get correct height
      const headerEl = headerRef.current?.firstElementChild as HTMLElement | null;
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const statusHeight = statusRef.current?.offsetHeight ?? 0;
      const totalOffset = headerHeight + statusHeight;
      
      // Only update if value changed (avoid unnecessary style recalcs)
      const currentOffset = getComputedStyle(document.documentElement)
        .getPropertyValue('--ybr-header-offset');
      const newOffset = `${totalOffset}px`;
      
      if (currentOffset !== newOffset) {
        document.documentElement.style.setProperty('--ybr-header-offset', newOffset);
      }
    });
  }, []);

  useLayoutEffect(() => {
    // Initial calculation
    calculateOffset();
    
    // Debounced ResizeObserver callback
    let timeoutId: NodeJS.Timeout;
    const debouncedCalculate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateOffset, 50);
    };
    
    const resizeObserver = new ResizeObserver(debouncedCalculate);
    // Observe the actual fixed header element (first child) to track its height correctly
    const headerEl = headerRef.current?.firstElementChild as HTMLElement | null;
    if (headerEl) resizeObserver.observe(headerEl);
    if (statusRef.current) resizeObserver.observe(statusRef.current);
    
    // Throttled window resize
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateOffset, 100);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [header, calculateOffset]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* iOS Status Bar Safe Area */}
      <div ref={statusRef} className="ybr-status-bar" />
      
      {/* Fixed Header */}
      {header && (
        <div ref={headerRef} className="ybr-page-header">
          {header}
        </div>
      )}
      
      {/* Main Content with Dynamic Padding */}
      <main 
        className={`flex-1 overflow-y-auto ybr-scroll-stable ${className}`}
        style={{ paddingTop: 'var(--ybr-header-offset, 112px)' }}
      >
        {children}
      </main>
    </div>
  );
}
